// Invite modal — the little panel that pops up when you invite a friend over
// P2P (docs/p2p.md). It shows the copyable join link and, when you're the host,
// a live count of who's connected. Built as a native <dialog> created on demand,
// mirroring RomPickerModal (no markup in index.html).
//
// Closing the modal (×) does NOT end the session — you can hand out the link and
// keep playing; reopening shows it again. Tearing a host session down from the
// UI isn't wired up yet (the "stop" concept is deferred; MunosScriptHost still
// exposes stopHosting() for when we revisit it).
//
// Two modes (open(url, mode)):
//   "host"  — you're the server: live peer count.
//   "relay" — you're a player (not the server) passing the invite along: a P2P
//             guest, or a plain client of a shared/official server. The link
//             drops a friend onto the same server you're playing on, straight —
//             not through you — same as forwarding the link you were given. No
//             peers of your own, so the count is hidden.

export interface ShareModalDeps {
  // Live remote-peer count, polled while the modal is open for the status line.
  peerCount: () => number;
}

export type ShareMode = "host" | "relay";

// How often the connected-count line refreshes while the modal is open.
const POLL_MS = 1000;

const HOST_BLURB = "Send this link to a friend to play together!";
const RELAY_BLURB = "Send this link to a friend to play together!";

export class ShareModal {
  private readonly dialog: HTMLDialogElement;
  private readonly urlInput: HTMLInputElement;
  private readonly copyBtn: HTMLButtonElement;
  private readonly blurb: HTMLElement;
  private readonly status: HTMLElement;
  private readonly deps: ShareModalDeps;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private copyResetTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(deps: ShareModalDeps) {
    this.deps = deps;

    this.dialog = document.createElement("dialog");
    this.dialog.className = "share-modal";
    this.dialog.setAttribute("aria-labelledby", "share-modal-title");

    const head = document.createElement("div");
    head.className = "modal-head";
    const title = document.createElement("h2");
    title.id = "share-modal-title";
    title.textContent = "Invite a friend";
    const close = document.createElement("button");
    close.type = "button";
    close.className = "modal-close";
    close.setAttribute("aria-label", "Close");
    close.textContent = "×";
    close.addEventListener("click", () => this.close());
    head.append(title, close);

    const body = document.createElement("div");
    body.className = "modal-body share-body";

    this.blurb = document.createElement("p");
    this.blurb.className = "share-blurb";
    this.blurb.textContent = HOST_BLURB;

    // The copyable link: a read-only field + a Copy button.
    const row = document.createElement("div");
    row.className = "share-link-row";
    this.urlInput = document.createElement("input");
    this.urlInput.type = "text";
    this.urlInput.readOnly = true;
    this.urlInput.className = "share-link-input";
    this.urlInput.setAttribute("aria-label", "Invite link");
    this.urlInput.addEventListener("focus", () => this.urlInput.select());
    this.copyBtn = document.createElement("button");
    this.copyBtn.type = "button";
    this.copyBtn.className = "share-copy-btn";
    this.copyBtn.textContent = "Copy";
    this.copyBtn.addEventListener("click", () => void this.copy());
    row.append(this.urlInput, this.copyBtn);

    this.status = document.createElement("p");
    this.status.className = "share-status";
    this.status.setAttribute("aria-live", "polite");

    body.append(this.blurb, row, this.status);
    this.dialog.append(head, body);
    document.body.append(this.dialog);
  }

  // Show the modal seeded with the link. "host" (default) is the server view
  // with a live peer count; "relay" is the guest pass-along view (see the mode
  // note at the top of this file).
  open(joinUrl: string, mode: ShareMode = "host"): void {
    this.urlInput.value = joinUrl;
    this.resetCopyLabel();
    this.applyMode(mode);
    if (!this.dialog.open) this.dialog.showModal();
    this.urlInput.focus();
  }

  // Swap the mode-specific chrome (blurb, status line) and start or stop the
  // peer-count poll. Called on every open so one modal instance can serve both
  // roles.
  private applyMode(mode: ShareMode): void {
    if (this.pollTimer !== null) { clearInterval(this.pollTimer); this.pollTimer = null; }
    if (mode === "relay") {
      this.blurb.textContent = RELAY_BLURB;
      this.status.textContent = "";
      return;
    }
    this.blurb.textContent = HOST_BLURB;
    this.refreshStatus();
    this.pollTimer = setInterval(() => this.refreshStatus(), POLL_MS);
  }

  close(): void {
    if (this.pollTimer !== null) { clearInterval(this.pollTimer); this.pollTimer = null; }
    if (this.dialog.open) this.dialog.close();
  }

  private refreshStatus(): void {
    const n = this.deps.peerCount();
    this.status.textContent = `${n} ${n === 1 ? "player" : "players"} connected to your server.`;
  }

  private async copy(): Promise<void> {
    const url = this.urlInput.value;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // Clipboard API blocked (insecure context / permission) — fall back to a
      // manual selection so the user can copy by hand.
      this.urlInput.focus();
      this.urlInput.select();
    }
    this.copyBtn.textContent = "Copied!";
    if (this.copyResetTimer !== null) clearTimeout(this.copyResetTimer);
    this.copyResetTimer = setTimeout(() => this.resetCopyLabel(), 1500);
  }

  private resetCopyLabel(): void {
    this.copyBtn.textContent = "Copy";
  }
}
